<script setup lang="ts">
import { computed } from 'vue'
import type { BetSelection } from '@nsftx/seven-client-retail-vue-integrator'
import { SCSimpleTable, SCIcon } from '@nsftx/seven-components'
import ticketCheckService from '@/modules/ticket-check/ticketCheckService'

interface TicketCheckDetailsBetSelectionProps {
  betSelections: BetSelection[]
}

const { betSelections } = defineProps<TicketCheckDetailsBetSelectionProps>()

const betSelectionsTableHeaders = [
  { text: '', value: 'statusColor' },
  { text: '', value: 'name' },
  { text: '', value: 'isBanker' },
  { text: '', value: 'startTime' },
  { text: '', value: 'marketName' },
  { text: '', value: 'outcomeName' },
  { text: '', value: 'oddValue' },
  { text: '', value: 'status' }
]

const getBetStatus = (betSelection: BetSelection) => betSelection.status?.toLocaleLowerCase()

const getBetStatusColor = computed(() => (betSelection: BetSelection) => {
  const betStatus = getBetStatus(betSelection)

  switch (betStatus) {
    case 'won':
      return '#388e3c'
    case 'open':
      return '#ffc107'
    case 'lost':
      return '#e53935'
    case 'void':
      return '#efefef99'
    default:
      return betStatus
  }
})

const getBetStatusIcon = computed(() => (betSelection: BetSelection) => {
  const betStatus = getBetStatus(betSelection)

  switch (betStatus) {
    case 'won':
      return 'n-i-check-e'
    case 'open':
      return 'n-i-clock-a'
    case 'lost':
      return 'n-i-close-d'
    case 'void':
      return 'n-i-whistle-a'
    default:
      return `n-i-${betStatus}`
  }
})

const getFormattedOdds = computed(() => (odds: number) => (odds / 10_000).toFixed(2))
</script>

<template>
  <div class="st-ticket-check-details-bets">
    <SCSimpleTable :headers="betSelectionsTableHeaders" :items="betSelections">
      <template #item-statusColor="{ item }">
        <span
          class="st-ticket-check-details-bets__bet__status-color"
          :style="{ backgroundColor: getBetStatusColor(item) }"
        >
        </span>
      </template>
      <template #item-name="{ item }">{{ item.event.derivedName }}</template>
      <template #item-isBanker="{ item }">
        <span v-if="item.isBanker" class="sc-btn sc-btn--primary sc-btn--banker"> B </span>
        <span v-else></span>
      </template>
      <template #item-startTime="{ item }">
        <span class="st-ticket-check-details-bets__bet__time">
          {{ ticketCheckService.getFormattedDate(item.event.startTime) }}
        </span>
      </template>
      <template #item-marketName="{ item }">{{ item.markets[0].derivedName }}</template>
      <template #item-outcomeName="{ item }">{{ item.markets[0].outcome.derivedName }}</template>
      <template #item-oddValue="{ item }">{{ getFormattedOdds(item.odds) }}</template>
      <template #item-status="{ item }">
        <SCIcon :color="getBetStatusColor(item)" :icon="getBetStatusIcon(item)"> </SCIcon>
      </template>
    </SCSimpleTable>
  </div>
</template>

<style lang="scss">
.st-ticket-check-details-bets {
  tr {
    &:first-of-type {
      th {
        padding: 0;
      }
    }

    &:not(:last-child) {
      border-bottom-width: 4px;
    }
  }

  td {
    &:first-of-type {
      padding: 0;
    }
  }

  &__bet {
    &__status-color {
      position: absolute;
      top: 0;
      left: 0;
      bottom: 0;
      width: 2px;
    }

    &__time {
      opacity: 0.6;
    }
  }

  .sc-btn--banker {
    display: inline;
    padding: 0.25rem 0.35rem;
    font-size: 0.75rem;
    font-weight: bold;
    cursor: default;
  }
}
</style>
